/*
 * Copyright (c) 2021 - 2026 All rights reserved by Ferenc Farkas <wolferi93@gmail.com>
 */

/**
 * @file CallProvider.js
 * @description
 * Foreground softphone layer for the in-app VoIP feature. Owns the SIP/WebRTC
 * client (jssip + react-native-webrtc) and the whole call state machine
 * (idle / ringing-in / ringing-out / active), metering the OUTBOUND leg against
 * the store's VoIP minute credits while leaving INCOMING calls free.
 *
 * Native-module safety: `react-native-webrtc` and `jssip` are loaded lazily and
 * guarded exactly like {@link module:services/callKeepClient} / voipPushKit —
 * until the dev-client / EAS build that bundles the WebRTC pod ships, the whole
 * layer is a safe no-op and never crashes Expo Go or a pre-prebuild binary.
 * `voipAudioAvailable()` reports whether the native audio stack loaded.
 *
 * The background/killed ringing path (CallKeep + PushKit) is wired at module
 * scope in `index.js`; this provider adds the FOREGROUND experience and, on
 * answer (native CallKit action OR the in-app banner), attaches two-way audio.
 *
 * Pure JavaScript (ESM), no TypeScript. iOS + Android.
 */

import React, {createContext, useCallback, useContext, useEffect, useMemo, useRef, useState} from 'react';
import {AppState, PermissionsAndroid, Platform} from 'react-native';
import Toast from 'react-native-toast-message';
import Observables from '@happy-gastro/global-observables';
import i18n from '../../i18n';
import HappyAlert from '../components/HappyAlert';
import {formatDialTarget} from './voipDialNumber';
import {getUserInfo} from './storage';
import {isCreditUnlimited, pickRestaurant, shouldHideCredit} from './unlimitedCredits';
import {reportCallConnected, endNativeCall, endNativeCallForRemoteEnd} from '../services/callKeepClient';
import {maybePromptFullScreenIntent} from '../services/fullScreenIntentPrompt';
import {
    recordPendingAnswer,
    readPendingAnswer,
    armPendingExpiry,
    clearPendingAnswer,
    matchesPushCaller,
    registerUaStarter,
    getPushCaller,
} from '../services/voipPendingAnswer';
import {
    getRtcConfig,
    lookupNumber,
    startCallSession,
    meterCallSession,
    endCallSession,
    getCreditStatus,
} from '../services/voipApi';
import {refreshVoipTokenRegistration} from '../services/voipPushKit';
import {connectVoipIdentitySocket} from '../services/voipIdentitySocket';
import {
    EMPTY_IDENTITY,
    isForCurrentCall,
    mergeIdentity,
    normalizeIdentity,
    shouldRunAiSearch,
    suggestionFromPush,
} from './voipCallerIdentity';

/* -------------------------------------------------------------------------- */
/*  Guarded native stack (react-native-webrtc + jssip)                          */
/* -------------------------------------------------------------------------- */

// `null` = not attempted yet, `false` = attempted & unavailable, object = loaded.
let JsSIP = null;
let audioLoaded = false;

/**
 * Load react-native-webrtc (registering the WebRTC globals jssip relies on) and
 * jssip exactly once, tolerating their absence. Mirrors the lazy/guarded require
 * pattern in callKeepClient.js so a build without the native pod is a no-op.
 * @returns {boolean} Whether the native audio stack is available.
 */
function loadSipStack() {
    if (JsSIP !== null) {
        return audioLoaded;
    }
    try {
        // eslint-disable-next-line global-require
        const webrtc = require('react-native-webrtc');
        if (typeof webrtc.registerGlobals === 'function') {
            webrtc.registerGlobals();
        }
        // eslint-disable-next-line global-require
        JsSIP = require('jssip').default || require('jssip');
        audioLoaded = true;
    } catch (error) {
        // eslint-disable-next-line no-console
        console.warn('[voip] SIP stack unavailable:', error?.message || error);
        JsSIP = false;
        audioLoaded = false;
    }
    return audioLoaded;
}

/**
 * Whether live two-way audio is available in this binary. Attempts the guarded
 * load on first read, so callers get an accurate flag once the provider mounts.
 * @returns {boolean}
 */
export function voipAudioAvailable() {
    if (JsSIP === null) {
        loadSipStack();
    }
    return audioLoaded;
}

const METER_INTERVAL_MS = 60000;
/** Bounded bootstrap retry delays (ms) after a transient rtc-config failure. */
const BOOTSTRAP_RETRY_DELAYS_MS = [5000, 15000, 60000];
/** How long dial() waits for a registering UA before offering the phone fallback. */
const REGISTER_WAIT_MS = 6000;
/** Socket outage after which a foreground resume rebuilds the UA instead of waiting for JsSIP recovery. */
const RECOVERY_GRACE_MS = 30000;

/**
 * @typedef {'idle'|'connecting'|'registered'|'failed'|'unconfigured'} RegState
 */

/** App version for the SIP User-Agent header (guarded — optional native module). */
function appVersionLabel() {
    try {
        // eslint-disable-next-line global-require
        const DeviceInfo = require('react-native-device-info').default || require('react-native-device-info');
        const v = DeviceInfo.getVersion();
        return v ? `HappyServices/${v}` : 'HappyServices';
    } catch (_error) {
        return 'HappyServices';
    }
}

/**
 * Request the microphone on Android (iOS prompts itself via getUserMedia).
 * @returns {Promise<boolean>} Whether recording is allowed.
 */
async function ensureMicPermission() {
    if (Platform.OS !== 'android') {
        return true;
    }
    try {
        const result = await PermissionsAndroid.request(PermissionsAndroid.PERMISSIONS.RECORD_AUDIO);
        return result === PermissionsAndroid.RESULTS.GRANTED;
    } catch (error) {
        // eslint-disable-next-line no-console
        console.warn('[voip] mic permission request failed:', error?.message || error);
        return false;
    }
}

/**
 * Map a jssip registrationFailed / disconnected event to a short reason.
 * @param {object} e jssip event.
 * @returns {'auth'|'network'|'config'}
 */
function classifyRegFailure(e) {
    const code = Number(e?.response?.status_code || 0);
    const cause = String(e?.cause || '').toLowerCase();
    if (code === 401 || code === 403 || code === 407 || cause.includes('authentication') || cause.includes('rejected')) {
        return 'auth';
    }
    if (code === 404 || code === 400 || cause.includes('address incomplete') || cause.includes('bad')) {
        return 'config';
    }
    return 'network';
}

/**
 * @typedef {'idle'|'ringing-in'|'ringing-out'|'active'} CallStatus
 */

const CallContext = createContext(null);

/**
 * Access the softphone call state + actions.
 * @returns {{
 *   status: CallStatus, remoteNumber: string, contact: (object|null),
 *   identity: {contact: (object|null), matches: object[], suggestion: (object|null), suggestionStatus: string},
 *   identitySearching: boolean, refreshIdentity: () => void,
 *   muted: boolean, durationSeconds: number, creditBalance: (number|null),
 *   hasConnection: boolean, audioAvailable: boolean, direction: (string|null),
 *   regState: RegState, regError: string, dialing: boolean,
 *   dial: (number: string) => Promise<{ok: boolean, reason: (string|null)}>, answer: () => void,
 *   hangup: () => void, toggleMute: () => void, refreshCredits: () => Promise<void>,
 *   restart: () => Promise<(object|null)>, sendDtmf: (digit: string) => boolean,
 * }}
 */
export function useVoip() {
    const ctx = useContext(CallContext);
    if (!ctx) {
        // A defensive stub so a consumer mounted outside the provider never throws.
        return {
            status: 'idle',
            remoteNumber: '',
            contact: null,
            identity: EMPTY_IDENTITY,
            identitySearching: false,
            refreshIdentity: () => {},
            muted: false,
            durationSeconds: 0,
            creditBalance: null,
            hasConnection: false,
            audioAvailable: false,
            direction: null,
            regState: 'idle',
            regError: '',
            dialing: false,
            dial: async () => ({ok: false, reason: 'unavailable'}),
            answer: () => {},
            hangup: () => {},
            toggleMute: () => {},
            refreshCredits: async () => {},
            restart: async () => null,
            sendDtmf: () => false,
        };
    }
    return ctx;
}

/**
 * Provider that hosts the softphone. Mount once at the navigation root (below
 * the theme + navigation providers) so every screen can `useVoip()` and the
 * foreground incoming banner / active-call bar can overlay the app.
 *
 * @param {{children: React.ReactNode, isLoggedIn?: boolean}} props `isLoggedIn`
 *   re-triggers the SIP bootstrap (and the iOS PushKit token upload) after login.
 * @returns {JSX.Element}
 */
export default function CallProvider({children, isLoggedIn = false}) {
    const [status, setStatus] = useState('idle');
    const [remoteNumber, setRemoteNumber] = useState('');
    const [identity, setIdentity] = useState(EMPTY_IDENTITY);
    const [identitySearching, setIdentitySearching] = useState(false);
    const contact = identity.contact;
    const [muted, setMuted] = useState(false);
    const [durationSeconds, setDurationSeconds] = useState(0);
    const [creditBalance, setCreditBalance] = useState(null);
    const [hasConnection, setHasConnection] = useState(false);
    const [regState, setRegStateRaw] = useState('idle');
    const [regError, setRegError] = useState('');
    const [dialing, setDialing] = useState(false);
    // VoIP credit is unlimited for this store: `hidden` = store user (no balance/low/top-up UI), `staff` = show a badge.
    const [creditUnlimited, setCreditUnlimited] = useState({unlimited: false, hidden: false});
    const userInfoRef = useRef(null);
    const creditUnlimitedRef = useRef(false);

    // Imperative call handles kept in refs (never trigger re-renders).
    const uaRef = useRef(null);
    const sessionRef = useRef(null);
    const sessionIdRef = useRef(null); // backend metered session id (outbound only)
    const directionRef = useRef(null);
    const answeredRef = useRef(false);
    const callIdRef = useRef(null); // CallKit callId for native sync
    const meterTimerRef = useRef(null);
    const durationTimerRef = useRef(null);
    const durationRef = useRef(0);
    const rtcRef = useRef({domain: '', iceServers: [], transport: 'sip-wss'});
    const restaurantIdRef = useRef(undefined);
    const answerRef = useRef(null); // latest answer(), callable from handleNewSession
    const regStateRef = useRef('idle');
    const dialingRef = useRef(false);
    const identityRef = useRef(EMPTY_IDENTITY); // latest caller identity (merge base)
    const identityEpochRef = useRef(0); // bumped per call so late lookups for finished calls are dropped
    const remoteNumberRef = useRef('');
    const authFailedRef = useRef(false); // last registration failed on credentials
    const disconnectedAtRef = useRef(0); // ms timestamp of the current socket outage (0 = none)
    const regErrorRef = useRef('');
    const uaBuildRef = useRef(null); // in-flight ensureUa() promise (single build)
    const buildGenRef = useRef(0); // bumped by restart() to discard stale builds
    const retryTimerRef = useRef(null);
    const retryCountRef = useRef(0);
    const scheduleRetryRef = useRef(null); // set once scheduleRetry() is defined

    const t = useCallback((key, fallback, opts) => i18n.t(key, {defaultValue: fallback, ...(opts || {})}), []);

    /** Resolve (and memo-cache) the operator's restaurant id for credit scoping. */
    const resolveRestaurantId = useCallback(async () => {
        if (restaurantIdRef.current !== undefined) {
            return restaurantIdRef.current;
        }
        try {
            const info = await getUserInfo();
            userInfoRef.current = info || null;
            const id = Array.isArray(info?.restaurants) && info.restaurants[0]
                ? String(info.restaurants[0]._id || info.restaurants[0])
                : null;
            restaurantIdRef.current = id;
            return id;
        } catch (_error) {
            restaurantIdRef.current = null;
            return null;
        }
    }, []);

    /* --------------------------- timers / cleanup ---------------------------- */

    const clearTimers = useCallback(() => {
        if (meterTimerRef.current) {
            globalThis.clearInterval(meterTimerRef.current);
            meterTimerRef.current = null;
        }
        if (durationTimerRef.current) {
            globalThis.clearInterval(durationTimerRef.current);
            durationTimerRef.current = null;
        }
    }, []);

    const setDirection = useCallback((dir) => {
        directionRef.current = dir;
    }, []);

    /** Merge a lookup / socket payload into the current identity (never wipes a confirmed contact). */
    const applyIdentity = useCallback((payload) => {
        const next = mergeIdentity(identityRef.current, payload);
        identityRef.current = next;
        setIdentity(next);
    }, []);

    /**
     * Identify the remote party: instant cached lookup, then (when nothing is
     * known and no search is already pending) a background AI web search.
     * Answers for a finished / replaced call are dropped via the epoch guard.
     */
    const identifyNumber = useCallback(async (number) => {
        const epoch = identityEpochRef.current;
        const alive = () => epoch === identityEpochRef.current;
        if (!number) {
            return;
        }
        try {
            const cached = await lookupNumber(String(number), 'cached');
            if (!alive()) {
                return;
            }
            applyIdentity(cached);
            if (!shouldRunAiSearch(identityRef.current)) {
                return;
            }
            setIdentitySearching(true);
            const ran = await lookupNumber(String(number), 'run');
            if (alive()) {
                applyIdentity(ran);
            }
        } catch (_error) {
            // Non-fatal — the number simply stays unidentified.
        } finally {
            if (alive()) {
                setIdentitySearching(false);
            }
        }
    }, [applyIdentity]);

    /** "Search again" — re-run the AI web search for the current remote party. */
    const refreshIdentity = useCallback(async () => {
        const number = remoteNumberRef.current;
        const epoch = identityEpochRef.current;
        if (!number) {
            return;
        }
        setIdentitySearching(true);
        try {
            const res = await lookupNumber(number, 'refresh');
            if (epoch === identityEpochRef.current) {
                applyIdentity(res);
            }
        } catch (_error) {
            // Keep whatever we already show.
        } finally {
            if (epoch === identityEpochRef.current) {
                setIdentitySearching(false);
            }
        }
    }, [applyIdentity]);

    /** Socket `voip:incoming` / `voip:caller-identified` → current call identity. */
    const onIdentityEvent = useCallback((_eventName, payload) => {
        if (!remoteNumberRef.current || !payload) {
            return;
        }
        const phone = payload.phone || payload.fromNumber || payload.from;
        const current = {
            callId: directionRef.current === 'outgoing' ? sessionIdRef.current : callIdRef.current,
            remoteNumber: remoteNumberRef.current,
        };
        if (!isForCurrentCall({callId: payload.callId, phone}, current)) {
            return;
        }
        applyIdentity(payload);
        if (payload.suggestionStatus !== 'pending') {
            setIdentitySearching(false);
        }
    }, [applyIdentity]);

    /** Reset all call state back to idle and reconcile the metered session. */
    const resetCall = useCallback(() => {
        clearTimers();
        const sid = sessionIdRef.current;
        if (sid) {
            // Outbound only — reconcile the final duration server-side (fire & forget).
            const durationSecondsFinal = durationRef.current;
            (async () => {
                const restaurantId = await resolveRestaurantId();
                endCallSession(sid, {durationSeconds: durationSecondsFinal, restaurantId}).catch(() => {});
            })();
            sessionIdRef.current = null;
        }
        sessionRef.current = null;
        answeredRef.current = false;
        callIdRef.current = null;
        directionRef.current = null;
        durationRef.current = 0;
        setStatus('idle');
        remoteNumberRef.current = '';
        identityEpochRef.current += 1;
        identityRef.current = EMPTY_IDENTITY;
        setRemoteNumber('');
        setIdentity(EMPTY_IDENTITY);
        setIdentitySearching(false);
        setMuted(false);
        setDurationSeconds(0);
    }, [clearTimers, resolveRestaurantId]);

    /** Begin the 1s duration ticker + (outbound) the 60s metering heartbeat. */
    const startActiveTimers = useCallback(() => {
        clearTimers();
        durationRef.current = 0;
        setDurationSeconds(0);
        durationTimerRef.current = globalThis.setInterval(() => {
            durationRef.current += 1;
            setDurationSeconds(durationRef.current);
        }, 1000);

        // Metering runs ONLY for outbound calls (incoming is free — no session).
        if (directionRef.current === 'outgoing' && sessionIdRef.current) {
            meterTimerRef.current = globalThis.setInterval(async () => {
                const sid = sessionIdRef.current;
                if (!sid) {
                    return;
                }
                try {
                    const restaurantId = await resolveRestaurantId();
                    const res = await meterCallSession(sid, restaurantId);
                    if (res && typeof res.balance === 'number') {
                        setCreditBalance(res.balance);
                    }
                    if (res && res.exhausted && !creditUnlimitedRef.current) {
                        Toast.show({type: 'error', text1: t('voip_credit_exhausted', 'VoIP egyenleg elfogyott — hívás bontása')});
                        // eslint-disable-next-line no-use-before-define
                        hangup();
                    }
                } catch (_error) {
                    // Never let a metering blip break the live call.
                }
            }, METER_INTERVAL_MS);
        }
        // `hangup` is declared below and is stable; referencing it here would
        // otherwise force a forward dependency cycle.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [clearTimers, resolveRestaurantId, t]);

    /* ----------------------------- session wiring ---------------------------- */

    const onSessionActive = useCallback(() => {
        answeredRef.current = true;
        setStatus('active');
        reportCallConnected(callIdRef.current);
        startActiveTimers();
    }, [startActiveTimers]);

    /**
     * Attach the jssip RTCSession lifecycle handlers. Two-way audio is carried
     * automatically by react-native-webrtc once the session is confirmed.
     * @param {object} session jssip RTCSession.
     */
    const attachSessionHandlers = useCallback((session) => {
        if (!session) {
            return;
        }
        // A superseded / replaced session must never touch the shared call state.
        const active = () => {
            if (sessionRef.current === session) {
                onSessionActive();
            }
        };
        // Remote end/cancel: also end the native call so CallKit / the Android
        // ringing notification never outlives the SIP session.
        const finish = () => {
            if (sessionRef.current !== session) {
                return;
            }
            try {
                endNativeCallForRemoteEnd(callIdRef.current, session.remote_identity?.uri?.user || '');
            } catch (_error) { /* noop */ }
            resetCall();
        };
        try {
            session.on('accepted', active);
            session.on('confirmed', active);
            session.on('ended', finish);
            session.on('failed', finish);
        } catch (_error) {
            // Older jssip event surfaces — ignore, lifecycle still drives resetCall.
        }
    }, [onSessionActive, resetCall]);

    /** Handle an inbound INVITE (foreground). No metering — incoming is free. */
    const handleNewSession = useCallback((event) => {
        const session = event && event.session;
        const originator = event && event.originator;
        if (!session || originator !== 'remote') {
            return; // outbound sessions are wired inline in dial()
        }
        if (dialingRef.current) {
            // We are mid-dial (UA wait / mic prompt): a competing INVITE is busy.
            try {
                session.terminate({status_code: 486});
            } catch (_error) { /* noop */ }
            return;
        }
        const from = session.remote_identity?.uri?.user
            || event?.request?.from?.uri?.user
            || '';
        const latched = readPendingAnswer().pending;
        if (sessionRef.current) {
            // A latched answer prefers the INVITE matching the push caller: if the
            // current one is an unanswered stranger, drop it in favour of the match.
            const current = sessionRef.current;
            const currentFrom = current.remote_identity?.uri?.user || '';
            const swap = latched && directionRef.current === 'incoming' && !answeredRef.current
                && !matchesPushCaller(currentFrom) && matchesPushCaller(from);
            if (!swap) {
                // Already on a call — reject the second INVITE.
                try {
                    session.terminate({status_code: 486});
                } catch (_error) { /* noop */ }
                return;
            }
            sessionRef.current = null;
            try {
                current.terminate({status_code: 486});
            } catch (_error) { /* noop */ }
        }
        sessionRef.current = session;
        directionRef.current = 'incoming';
        answeredRef.current = false;
        setDirection('incoming');
        remoteNumberRef.current = String(from || '');
        identityEpochRef.current += 1;
        // Seed with the wake push's unverified guess (never the title) until the lookup answers.
        const pushCaller = getPushCaller();
        const provisional = pushCaller && matchesPushCaller(from) ? suggestionFromPush(pushCaller.suggestedName) : null;
        identityRef.current = provisional ? normalizeIdentity({suggestion: provisional}) : EMPTY_IDENTITY;
        setIdentity(identityRef.current);
        setIdentitySearching(false);
        setRemoteNumber(String(from || ''));
        setStatus('ringing-in');
        attachSessionHandlers(session);

        // Screen-pop: enrich the caller identity from the backend directory.
        if (from) {
            identifyNumber(String(from));
        }
        Observables.call('voip:incoming', {number: String(from || '')});

        // Answer was tapped on the native UI before this INVITE arrived (killed
        // app): auto-answer now and bind the native callId for hangup/mute.
        // A non-matching caller leaves the latch armed for the right INVITE.
        if (latched && matchesPushCaller(from)) {
            clearPendingAnswer();
            if (latched.callId) {
                callIdRef.current = latched.callId;
            }
            globalThis.setTimeout(() => answerRef.current && answerRef.current(), 0);
        }
    }, [attachSessionHandlers, setDirection, identifyNumber]);

    /* ------------------------------ UA lifecycle ----------------------------- */

    /** Update the registration state (ref + React state) and its short error. */
    const setReg = useCallback((state, error = '') => {
        regStateRef.current = state;
        regErrorRef.current = error;
        setRegStateRaw(state);
        setRegError(error);
    }, []);

    /** Stop and forget the current UA (ua.stop() un-registers this contact only). */
    const teardownUa = useCallback(() => {
        const ua = uaRef.current;
        uaRef.current = null;
        if (!ua) {
            return;
        }
        try {
            ua.removeAllListeners?.();
        } catch (_error) { /* noop */ }
        try {
            // stop() unregisters only THIS contact; unregister({all}) would wipe
            // every binding on the AOR (e.g. the admin web softphone).
            ua.stop();
        } catch (error) {
            // eslint-disable-next-line no-console
            console.warn('[voip] ua.stop failed:', error?.message || error);
        }
    }, []);

    /**
     * Fetch the rtc-config and build + start a fresh UA. Single-flight is
     * enforced by {@link ensureUa}; `gen` lets restart() discard a stale build.
     * @param {number} gen Build generation captured at start.
     * @returns {Promise<object|null>}
     */
    const buildUa = useCallback(async (gen) => {
        setReg('connecting');
        let cfg;
        try {
            cfg = await getRtcConfig();
        } catch (error) {
            if (gen !== buildGenRef.current) {
                return null;
            }
            setHasConnection(false);
            if (error?.status === 404) {
                setReg('unconfigured', 'config');
            } else if (error?.code === 'AUTH_REQUIRED') {
                // Not signed in (yet) — the isLoggedIn effect restarts the bootstrap.
                setReg('idle');
            } else {
                // eslint-disable-next-line no-console
                console.warn('[voip] rtc-config failed:', error?.status || error?.message || error);
                setReg('failed', 'network');
                scheduleRetryRef.current?.();
            }
            return null;
        }
        if (gen !== buildGenRef.current) {
            return null;
        }
        if (!cfg || !cfg.rtc) {
            setHasConnection(false);
            setReg('unconfigured', 'config');
            return null;
        }
        setHasConnection(true);
        retryCountRef.current = 0;
        rtcRef.current = {
            domain: cfg.rtc.domain || '',
            iceServers: Array.isArray(cfg.rtc.iceServers) ? cfg.rtc.iceServers : [],
            transport: cfg.transport || 'sip-wss',
            dialPlan: cfg.rtc.dialPlan || cfg.dialPlan || null,
        };

        // Twilio Voice needs the Twilio RN SDK (not bundled) — SIP is the primary
        // path. Degrade gracefully: report a connection but no UA to place calls.
        if (cfg.transport === 'twilio') {
            setReg('idle');
            return null;
        }
        if (!cfg.rtc.wsServer || !cfg.rtc.sipUri) {
            // eslint-disable-next-line no-console
            console.warn('[voip] incomplete SIP config (wsServer/sipUri missing)');
            setReg('failed', 'config');
            return null;
        }

        try {
            const socket = new JsSIP.WebSocketInterface(cfg.rtc.wsServer);
            const ua = new JsSIP.UA({
                sockets: [socket],
                uri: cfg.rtc.sipUri,
                password: cfg.rtc.password || '',
                display_name: cfg.rtc.displayName || cfg.callerId || '',
                register: true,
                register_expires: 300,
                connection_recovery_min_interval: 2,
                connection_recovery_max_interval: 30,
                session_timers: false,
                user_agent: appVersionLabel(),
            });
            ua.on('newRTCSession', handleNewSession);
            ua.on('connected', () => {
                disconnectedAtRef.current = 0;
                if (uaRef.current === ua && regStateRef.current !== 'registered') {
                    setReg('connecting');
                }
            });
            ua.on('disconnected', () => {
                // jssip auto-recovers the socket (connection_recovery_*); just reflect it.
                if (uaRef.current === ua) {
                    if (!disconnectedAtRef.current) {
                        disconnectedAtRef.current = Date.now();
                    }
                    setReg('failed', 'network');
                }
            });
            // Android 14+: once per session after registration, ask for the
            // full-screen-call permission (locked-screen ringing) if denied.
            ua.on('registered', () => {
                if (uaRef.current !== ua) {
                    return;
                }
                authFailedRef.current = false;
                setReg('registered');
                if (AppState.currentState === 'active') {
                    maybePromptFullScreenIntent().catch(() => {});
                }
            });
            ua.on('unregistered', () => {
                if (uaRef.current === ua && regStateRef.current === 'registered') {
                    setReg('connecting');
                }
            });
            ua.on('registrationFailed', (e) => {
                if (uaRef.current !== ua) {
                    return;
                }
                const reason = classifyRegFailure(e);
                // eslint-disable-next-line no-console
                console.warn('[voip] registrationFailed:', e?.response?.status_code || e?.cause || reason);
                // Credentials rejected: never auto-retry (provider lockout risk).
                authFailedRef.current = reason === 'auth';
                setReg('failed', reason);
            });
            authFailedRef.current = false;
            disconnectedAtRef.current = 0;
            uaRef.current = ua;
            ua.start();
            return ua;
        } catch (error) {
            // eslint-disable-next-line no-console
            console.warn('[voip] UA start failed:', error?.message || error);
            uaRef.current = null;
            setReg('failed', 'config');
            return null;
        }
    }, [handleNewSession, setReg]);

    /**
     * Ensure the SIP UA is created + registered from the backend rtc-config.
     * Single-flight: concurrent triggers (bootstrap, AppState, wake-push, dial,
     * answer) share one build. Returns the UA, or null when audio is
     * unavailable / no connection / Twilio.
     * @returns {Promise<object|null>}
     */
    const ensureUa = useCallback(() => {
        if (!loadSipStack()) {
            return Promise.resolve(null);
        }
        if (uaRef.current) {
            return Promise.resolve(uaRef.current);
        }
        if (!uaBuildRef.current) {
            const gen = buildGenRef.current;
            const inflight = buildUa(gen).finally(() => {
                if (uaBuildRef.current === inflight) {
                    uaBuildRef.current = null;
                }
            });
            uaBuildRef.current = inflight;
        }
        return uaBuildRef.current;
    }, [buildUa]);

    /** Schedule a bounded-backoff bootstrap retry (5s, 15s, 60s) after a transient failure. */
    const scheduleRetry = useCallback(() => {
        if (retryTimerRef.current || retryCountRef.current >= BOOTSTRAP_RETRY_DELAYS_MS.length) {
            return;
        }
        const delay = BOOTSTRAP_RETRY_DELAYS_MS[retryCountRef.current];
        retryCountRef.current += 1;
        retryTimerRef.current = globalThis.setTimeout(() => {
            retryTimerRef.current = null;
            ensureUa().catch(() => {});
        }, delay);
    }, [ensureUa]);
    scheduleRetryRef.current = scheduleRetry;

    /**
     * Tear down the current UA (unregister + stop) and rebuild it from a fresh
     * rtc-config. Call after saving / deleting a connection or from "Retry".
     * @returns {Promise<object|null>} The new UA, or null when none could be built.
     */
    const restart = useCallback(async () => {
        buildGenRef.current += 1;
        uaBuildRef.current = null;
        retryCountRef.current = 0;
        authFailedRef.current = false;
        if (retryTimerRef.current) {
            globalThis.clearTimeout(retryTimerRef.current);
            retryTimerRef.current = null;
        }
        teardownUa();
        setReg('idle');
        return ensureUa();
    }, [teardownUa, setReg, ensureUa]);

    /* -------------------------------- actions -------------------------------- */

    /** Arm the pending-answer timeout: end the native call + toast on expiry. */
    const armLatchExpiry = useCallback(() => {
        armPendingExpiry((expired) => {
            try {
                endNativeCall(expired.callId || callIdRef.current);
            } catch (_error) { /* noop */ }
            Toast.show({type: 'error', text1: t('voip_call_failed_connect', 'A hívást nem sikerült felvenni')});
        });
    }, [t]);

    /**
     * Wait (bounded) for the UA to finish registering.
     * @param {number} timeoutMs Max wait.
     * @returns {Promise<boolean>} Whether the UA is registered.
     */
    const waitForRegistered = useCallback(async (timeoutMs) => {
        const deadline = Date.now() + timeoutMs;
        while (Date.now() < deadline) {
            if (regStateRef.current === 'registered') {
                return true;
            }
            if (regStateRef.current === 'failed' && ['auth', 'config'].includes(regErrorRef.current)) {
                return false;
            }
            // eslint-disable-next-line no-await-in-loop
            await new Promise((resolve) => { globalThis.setTimeout(resolve, 250); });
        }
        return regStateRef.current === 'registered';
    }, []);

    /**
     * Place an OUTBOUND call: make sure the UA is up + registered, check the
     * microphone, open a metered session (402 → block + top-up prompt), then
     * dial over SIP. Metering starts once the call goes active.
     * @param {string} number Destination phone number.
     * @returns {Promise<{ok: boolean, reason: (string|null)}>} `reason` is one of
     *   busy | unavailable | twilio | no-connection | not-registered | mic-denied | credit | failed.
     */
    const dialInner = useCallback(async (number) => {
        const to = String(number || '').trim();
        if (!to || status !== 'idle') {
            return {ok: false, reason: 'busy'};
        }
        if (!loadSipStack()) {
            Toast.show({type: 'error', text1: t('voip_audio_unavailable', 'A hanghívás ebben a buildben nem elérhető')});
            return {ok: false, reason: 'unavailable'};
        }
        const ua = await ensureUa();
        if (!ua) {
            if (rtcRef.current.transport === 'twilio' && regStateRef.current === 'idle') {
                Toast.show({type: 'error', text1: t('voip_twilio_unsupported', 'A Twilio hívás mobilon nem támogatott')});
                return {ok: false, reason: 'twilio'};
            }
            if (regStateRef.current === 'unconfigured') {
                Toast.show({type: 'error', text1: t('voip_no_connection', 'Nincs beállított VoIP kapcsolat')});
                return {ok: false, reason: 'no-connection'};
            }
            return {ok: false, reason: 'not-registered'};
        }
        if (regStateRef.current !== 'registered' && !(await waitForRegistered(REGISTER_WAIT_MS))) {
            return {ok: false, reason: 'not-registered'};
        }
        if (!(await ensureMicPermission())) {
            HappyAlert.show({
                type: 'warning',
                message: t('voip_mic_denied', 'A híváshoz mikrofon-hozzáférés szükséges'),
            });
            return {ok: false, reason: 'mic-denied'};
        }

        remoteNumberRef.current = to;
        identityEpochRef.current += 1;
        identityRef.current = EMPTY_IDENTITY;
        setIdentity(EMPTY_IDENTITY);
        setIdentitySearching(false);
        setRemoteNumber(to);
        setDirection('outgoing');
        answeredRef.current = false;
        setStatus('ringing-out');

        // 1) Metered session — HTTP 402 blocks the call and prompts a top-up.
        try {
            const restaurantId = await resolveRestaurantId();
            const session = await startCallSession({to, direction: 'outgoing', restaurantId});
            sessionIdRef.current = session?.sessionId || null;
            if (session && typeof session.balance === 'number') {
                setCreditBalance(session.balance);
            }
        } catch (error) {
            let reason = 'failed';
            if (error?.status === 402) {
                reason = 'credit';
                Toast.show({type: 'error', text1: t('voip_out_of_credit', 'Nincs elég VoIP egyenleg — tölts fel a híváshoz')});
                Observables.call('voip:needTopup', {});
            } else {
                Toast.show({type: 'error', text1: t('voip_call_failed', 'A hívás indítása sikertelen')});
            }
            resetCall();
            return {ok: false, reason};
        }

        // Screen-pop for the outbound identity too.
        identifyNumber(to);

        // 2) Place the SIP call (number rewritten per the provider's dial plan).
        try {
            const {domain, dialPlan} = rtcRef.current;
            const user = formatDialTarget(to, dialPlan).replace(/#/g, '%23');
            const target = to.startsWith('sip:') ? to : `sip:${user}@${domain}`;
            const rtcSession = ua.call(target, {
                mediaConstraints: {audio: true, video: false},
                pcConfig: {iceServers: rtcRef.current.iceServers || []},
            });
            sessionRef.current = rtcSession;
            attachSessionHandlers(rtcSession);
        } catch (error) {
            // eslint-disable-next-line no-console
            console.warn('[voip] ua.call failed:', error?.message || error);
            Toast.show({type: 'error', text1: t('voip_call_failed', 'A hívás indítása sikertelen')});
            resetCall();
            return {ok: false, reason: 'failed'};
        }

        return {ok: true, reason: null};
    }, [status, ensureUa, waitForRegistered, attachSessionHandlers, resolveRestaurantId, resetCall, setDirection, identifyNumber, t]);

    /**
     * Re-entrancy-safe dial: `status` stays 'idle' while the UA registers and the
     * mic prompt is open, so a ref gates double taps and competing INVITEs.
     * @param {string} number Destination phone number.
     * @returns {Promise<{ok: boolean, reason: (string|null)}>}
     */
    const dial = useCallback(async (number) => {
        if (dialingRef.current) {
            return {ok: false, reason: 'busy'};
        }
        dialingRef.current = true;
        setDialing(true);
        try {
            return await dialInner(number);
        } finally {
            dialingRef.current = false;
            setDialing(false);
        }
    }, [dialInner]);

    /**
     * Answer the current INCOMING call and attach two-way audio. Idempotent —
     * safe to call from both the native CallKit action and the in-app banner.
     * When the SIP INVITE has not arrived yet (iOS in-app Answer, killed app) the
     * intent is latched and applied the moment the INVITE lands.
     * @returns {void}
     */
    const answer = useCallback(() => {
        const session = sessionRef.current;
        if (answeredRef.current) {
            return;
        }
        if (!session) {
            if (callIdRef.current || getPushCaller()) {
                recordPendingAnswer({callId: callIdRef.current});
                armLatchExpiry();
                ensureUa().catch(() => {});
            }
            return;
        }
        if (!loadSipStack()) {
            return;
        }
        answeredRef.current = true;
        (async () => {
            if (!(await ensureMicPermission())) {
                HappyAlert.show({
                    type: 'warning',
                    message: t('voip_mic_denied', 'A híváshoz mikrofon-hozzáférés szükséges'),
                });
                try {
                    session.terminate({status_code: 603});
                } catch (_error) { /* noop */ }
                resetCall();
                return;
            }
            if (sessionRef.current !== session) {
                return; // the caller hung up while the permission prompt was open
            }
            try {
                session.answer({
                    mediaConstraints: {audio: true, video: false},
                    pcConfig: {iceServers: rtcRef.current.iceServers || []},
                });
            } catch (error) {
                // eslint-disable-next-line no-console
                console.warn('[voip] session.answer failed:', error?.message || error);
                // If the answer fails, fall back to tearing the call down.
                resetCall();
            }
        })();
    }, [resetCall, armLatchExpiry, ensureUa, t]);

    useEffect(() => {
        answerRef.current = answer;
    }, [answer]);

    /**
     * Hang up / reject the current call (local, remote and native CallKit all
     * funnel here). Terminates the SIP session and mirrors the native call end.
     * @returns {void}
     */
    const hangup = useCallback(() => {
        const session = sessionRef.current;
        try {
            if (session && !session.isEnded?.()) {
                session.terminate();
            }
        } catch (_error) { /* noop */ }
        try {
            endNativeCall(callIdRef.current);
        } catch (_error) { /* noop */ }
        resetCall();
    }, [resetCall]);

    /**
     * Toggle the local microphone mute on the active session and mirror it to
     * the native CallKit mute state.
     * @returns {void}
     */
    const toggleMute = useCallback(() => {
        const session = sessionRef.current;
        const next = !muted;
        try {
            if (session) {
                if (next) {
                    session.mute({audio: true});
                } else {
                    session.unmute({audio: true});
                }
            }
        } catch (_error) { /* noop */ }
        setMuted(next);
    }, [muted]);

    /**
     * Send a DTMF digit on the active call (IVR menus, extensions).
     * @param {string} digit One of 0-9 * # A-D.
     * @returns {boolean} Whether the tone was sent.
     */
    const sendDtmf = useCallback((digit) => {
        const session = sessionRef.current;
        if (!session || !answeredRef.current || !/^[0-9*#A-D]$/i.test(String(digit))) {
            return false;
        }
        try {
            session.sendDTMF(String(digit));
            return true;
        } catch (error) {
            // eslint-disable-next-line no-console
            console.warn('[voip] sendDTMF failed:', error?.message || error);
            return false;
        }
    }, []);

    /** Refresh the store's VoIP credit balance (dialer + settings header). */
    const refreshCredits = useCallback(async () => {
        try {
            const restaurantId = await resolveRestaurantId();
            const res = await getCreditStatus(restaurantId);
            const user = userInfoRef.current;
            const restaurant = pickRestaurant(user, restaurantId);
            const unlimited = isCreditUnlimited({restaurant, type: 'voip', status: res});
            const hidden = shouldHideCredit({user, restaurant, type: 'voip', status: res});
            creditUnlimitedRef.current = unlimited;
            setCreditUnlimited({unlimited, hidden});
            if (unlimited) {
                setCreditBalance(null);
            } else if (res && typeof res.credits === 'number') {
                setCreditBalance(res.credits);
            }
        } catch (_error) {
            // Non-fatal — balance simply stays unknown.
        }
    }, [resolveRestaurantId]);

    /* ------------------------------- effects --------------------------------- */

    // Bridge the native CallKit actions (index.js Observables) into the softphone.
    useEffect(() => {
        const onAnswer = (info) => {
            if (info?.callId) {
                callIdRef.current = info.callId;
            }
            if (sessionRef.current) {
                clearPendingAnswer();
                answer();
                return;
            }
            // No SIP session yet (killed app: UA not registered / INVITE in flight).
            // Latch the intent, make sure the UA is starting, and give up cleanly
            // if the INVITE never shows up.
            recordPendingAnswer(info);
            armLatchExpiry();
            ensureUa().catch(() => {});
        };
        const onEnd = () => {
            clearPendingAnswer();
            hangup();
        };
        const onMute = (payload) => {
            const session = sessionRef.current;
            const wantMuted = !!payload?.muted;
            try {
                if (session) {
                    if (wantMuted) {
                        session.mute({audio: true});
                    } else {
                        session.unmute({audio: true});
                    }
                }
            } catch (_error) { /* noop */ }
            setMuted(wantMuted);
        };

        Observables.register('voip:answer', onAnswer);
        Observables.register('voip:end', onEnd);
        Observables.register('voip:mute', onMute);
        return () => {
            Observables.remove('voip:answer', onAnswer);
            Observables.remove('voip:end', onEnd);
            Observables.remove('voip:mute', onMute);
        };
    }, [answer, hangup, ensureUa, armLatchExpiry]);

    // Register the SIP UA when the app is foregrounded so live incoming calls +
    // outbound dialing work. Deferred + guarded — a missing pod is a no-op.
    useEffect(() => {
        let cancelled = false;
        // Wake-push hook: lets voipPushKit/callKeepClient start SIP registration
        // immediately (or consumes a request made before this provider mounted).
        const unregisterStarter = registerUaStarter(() => ensureUa());

        // Cold start: an Answer tapped before we mounted is latched at module level.
        const {pending, expired} = readPendingAnswer();
        if (expired) {
            endNativeCall(expired.callId);
        } else if (pending) {
            if (pending.callId) {
                callIdRef.current = pending.callId;
            }
            armLatchExpiry();
            ensureUa().catch(() => {});
        }
        const bootstrap = () => {
            if (cancelled) {
                return;
            }
            ensureUa().catch(() => {});
            refreshCredits().catch(() => {});
        };
        const timer = globalThis.setTimeout(bootstrap, 1500);

        const sub = AppState.addEventListener('change', (state) => {
            if (state !== 'active') {
                return;
            }
            const ua = uaRef.current;
            if (!ua) {
                retryCountRef.current = 0;
                ensureUa().catch(() => {});
                return;
            }
            if (sessionRef.current) {
                return; // never disturb a live call
            }
            if (authFailedRef.current) {
                return; // only the explicit Retry / a settings save retries bad credentials
            }
            try {
                if (ua.isConnected()) {
                    if (!ua.isRegistered()) {
                        setReg('connecting');
                        ua.register();
                    }
                } else if (disconnectedAtRef.current && Date.now() - disconnectedAtRef.current > RECOVERY_GRACE_MS) {
                    // Outage outlasted JsSIP's own recovery — rebuild from a fresh config.
                    restart().catch(() => {});
                }
            } catch (error) {
                // eslint-disable-next-line no-console
                console.warn('[voip] foreground re-register failed:', error?.message || error);
            }
        });

        return () => {
            cancelled = true;
            globalThis.clearTimeout(timer);
            sub?.remove?.();
            unregisterStarter();
        };
    }, [ensureUa, refreshCredits, armLatchExpiry, restart, setReg]);

    // Login → (re)start SIP registration + upload the iOS PushKit token. The
    // one-shot boot timer above often fires before the user is signed in, which
    // left the softphone unconfigured until the next restart. Logout tears down.
    const wasLoggedInRef = useRef(false);
    useEffect(() => {
        if (isLoggedIn) {
            wasLoggedInRef.current = true;
            retryCountRef.current = 0;
            ensureUa().catch(() => {});
            refreshCredits().catch(() => {});
            refreshVoipTokenRegistration().catch(() => {});
        } else if (wasLoggedInRef.current) {
            wasLoggedInRef.current = false;
            buildGenRef.current += 1;
            uaBuildRef.current = null;
            teardownUa();
            setHasConnection(false);
            setReg('idle');
        }
    }, [isLoggedIn, ensureUa, refreshCredits, teardownUa, setReg]);

    // Tear the UA + timers down on unmount so we never leak a socket/timer.
    useEffect(() => () => {
        clearTimers();
        if (retryTimerRef.current) {
            globalThis.clearTimeout(retryTimerRef.current);
            retryTimerRef.current = null;
        }
        teardownUa();
    }, [clearTimers, teardownUa]);

    // Background caller identification pushed by the backend to my user room.
    useEffect(() => {
        if (!isLoggedIn) {
            return undefined;
        }
        let cancelled = false;
        let handle = null;
        (async () => {
            const info = await getUserInfo();
            const userId = info?._id || info?.id;
            if (cancelled || !userId) {
                return;
            }
            handle = connectVoipIdentitySocket({userId: String(userId), onEvent: onIdentityEvent});
        })().catch(() => {});
        return () => {
            cancelled = true;
            if (handle) {
                handle.close();
            }
        };
    }, [isLoggedIn, onIdentityEvent]);

    const value = useMemo(() => ({
        status,
        remoteNumber,
        contact,
        identity,
        identitySearching,
        refreshIdentity,
        muted,
        durationSeconds,
        creditBalance,
        creditUnlimited: creditUnlimited.unlimited,
        creditHidden: creditUnlimited.hidden,
        hasConnection,
        regState,
        regError,
        dialing,
        audioAvailable: voipAudioAvailable(),
        direction: directionRef.current,
        dial,
        answer,
        hangup,
        toggleMute,
        refreshCredits,
        restart,
        sendDtmf,
    }), [status, remoteNumber, contact, identity, identitySearching, refreshIdentity, muted, durationSeconds, creditBalance, creditUnlimited, hasConnection, regState, regError, dialing, dial, answer, hangup, toggleMute, refreshCredits, restart, sendDtmf]);

    return <CallContext.Provider value={value}>{children}</CallContext.Provider>;
}
